import {
  getBalanceChecker,
  getCard,
  getCardCaptcha,
  getCardPin,
} from "123tcs-common/api/services/cardbase/BalanceCheckerTransactions";
import config from "123tcs-common/config";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useEditorSettings } from "../../contexts/EditorSettings";
import { useLanguage } from "../../contexts/Language";
import { useSimulation } from "../../contexts/Simulation";
import BalanceCheckerForm from "./BalanceCheckerForm";
import BalanceCheckerResult from "./BalanceCheckerResult";

/**
 * The balance checker component allows cards to be checked for their balance and
 * transaction history using the verification code and/or captcha for verification.
 * @returns The balance checker component.
 */
function BalanceChecker() {
  // Contexts
  const { t: translate } = useTranslation();
  const { simulateEditorSettings } = useSimulation();
  const { switchLanguage } = useLanguage();
  const { settings } = useEditorSettings();
  const params = useParams();

  // States
  const [card, setCard] = useState({});
  const [cardNumber, setCardNumber] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [showCardInfo, setShowCardInfo] = useState(false);
  const [errors, setErrors] = useState(
    settings.errors.length > 0
      ? settings.errors.map((error) => {
          return translate(`errors.${error}`);
        })
      : [],
  );
  const [balanceChecker, setBalanceChecker] = useState({ id: params.id });

  // Effects
  useEffect(() => {
    const getNewBalanceChecker = async () => {
      try {
        if (simulateEditorSettings) {
          setBalanceChecker({
            id: "SIMULATION",
            useVerificationCode: settings.showVerificationCode,
            useCaptcha: settings.showCaptcha,
          });
        } else {
          const responseContent = await getBalanceChecker(params.id);

          setBalanceChecker({ id: params.id, ...responseContent });
          switchLanguage(responseContent.languageCode.toLowerCase());
        }
      } catch (error) {
        console.log("404");
      }
    };

    getNewBalanceChecker();
  }, [params.id, settings]);

  useEffect(() => {
    if (simulateEditorSettings) {
      setErrors(
        settings.errors.map((error) => {
          return translate(`errors.${error}`);
        }),
      );
    }
  }, [settings]);

  // Functions
  const readCard = async () => {
    const collectedErrors = [];

    try {
      // Collect error if card number is not filled in.
      if (!cardNumber) {
        collectedErrors.push(translate("errors.missingCardNumber"));
      }

      // If the prerequisites are met request the card info and transaction history.
      if (collectedErrors.length === 0) {
        const trimmedCardNumber = cardNumber.trim();
        let cardResponse;

        if (balanceChecker.useCaptcha) {
          cardResponse = await getCardCaptcha(
            balanceChecker.id,
            trimmedCardNumber,
            captchaToken,
          );
        } else if (balanceChecker.useVerificationCode) {
          cardResponse = await getCardPin(
            balanceChecker.id,
            trimmedCardNumber,
            pinCode.trim(),
          );
        } else {
          cardResponse = await getCard(balanceChecker.id, trimmedCardNumber);
        }

        setCard({ ...cardResponse, number: trimmedCardNumber });
        setShowCardInfo(true);
      }
    } catch (error) {
      collectedErrors.push(translate("errors.invalidCardNumber")); // invalid card number error code
    }

    setErrors(collectedErrors);
  };

  const handleBack = () => {
    setCardNumber("");
    setPinCode("");
    setShowCardInfo(false);
  };

  // Render
  return (
    <>
      {!simulateEditorSettings && (
        <link
          rel="stylesheet"
          type="text/css"
          href={`${config.cardbase.url}/stylesheet/${params.id}.css`}
        />
      )}

      <div className="flex-center position-ref full-height">
        <div className="content">
          <div className="m-b-md">
            {!showCardInfo && (
              <BalanceCheckerForm
                id="BalanceCheckerForm"
                onSubmit={simulateEditorSettings ? () => {} : readCard}
                cardNumber={cardNumber}
                setCardNumber={setCardNumber}
                pinCode={pinCode}
                setPinCode={setPinCode}
                captchaToken={captchaToken}
                setCaptchaToken={setCaptchaToken}
                balanceChecker={balanceChecker}
                errors={errors}
              />
            )}

            {showCardInfo && (
              <BalanceCheckerResult
                card={card}
                showEndOfLife={balanceChecker.showEndOfLife}
                onBack={simulateEditorSettings ? () => {} : handleBack}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default BalanceChecker;
